import { ApolloClient, ApolloLink, CombinedGraphQLErrors, InMemoryCache } from '@apollo/client/core'
import { WEBSOCKET_RECONNECT_INTERVAL_MS } from '@/config/config.ts'
import { DEFAULT_ENVIRONMENT, getApiUrlByEnv, getDefaultClientEnv, getWebsocketUrlByEnv, } from '@/config/env.const.ts'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { HttpLink } from '@apollo/client/link/http'
import { ErrorLink } from '@apollo/client/link/error'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { BackendErrorCode, Environment } from '@/enums.ts'
import type { DocumentNode } from 'graphql/language/ast'
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'
import { useUserStore } from '@/stores/user.store.ts'
import * as Sentry from '@sentry/vue'
import { useTranslations } from '@/composables/useTranslations.ts'
import { useToast } from 'vue-toastification'
import { SKIP_ERROR_TOAST_FOR_OPERATIONS } from '@/config/error.const.ts'
import { provideApolloClient } from '@vue/apollo-composable'

const splitTestFunc = ({ query }: { query: DocumentNode }) => {
  const definition = getMainDefinition(query)
  return (definition.kind === 'OperationDefinition' && definition.operation === 'subscription')
}

// TODO: REVALIDATE SOLUTION!
const errorLink = new ErrorLink(({ operation, result, error }) => {
  if (DEFAULT_ENVIRONMENT !== Environment.Production) {
    console.error('GraphQL Error | operation:', operation)
    console.error('GraphQL Error | response:', result)
    console.error('GraphQL Error | error:', error)
    console.log("\n")
  }

  const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : []

  const { isAuthorized, getUserInfo } = useUserStore()
  const toast = useToast()
  const { t } = useTranslations()
  const userInfo = getUserInfo()

  Sentry.setUser({ id: isAuthorized ? userInfo.id as number : 'guest' })
  Sentry.setContext('operation', operation as unknown as Record<string, unknown>)
  Sentry.setContext('response', result as unknown as Record<string, unknown>)
  Sentry.setContext('errors', graphQLErrors as unknown as Record<string, unknown>)
  Sentry.captureMessage(JSON.stringify(graphQLErrors))

  if (!SKIP_ERROR_TOAST_FOR_OPERATIONS.includes(operation.operationName ?? '')) {
    const errorCodes: string[] = []

    for (const graphQLError of graphQLErrors) {
      errorCodes.push(graphQLError.message)
    }

    for (const errorCode of Array.from(new Set(errorCodes))) {
      if (Object.values(BackendErrorCode).includes(errorCode as BackendErrorCode)) {
        toast.error(t(errorCode))
      } else {
        console.error('GraphqlErrorLink | Unknown error', errorCode)
        toast.error(BackendErrorCode.UnknownError)
      }
    }
  }
})

export const WSLinkStore = Object.freeze(
  Object
    .values(Environment)
    .reduce<Record<Environment, GraphQLWsLink>>((acc, env: Environment)=> {
      acc[env] = new GraphQLWsLink(createClient({
        url: getWebsocketUrlByEnv(env),
        retryAttempts: 100,
        shouldRetry (err: unknown): boolean {
          console.warn('shouldRetry | err:', err)
          return true
        },
        async retryWait(retries: number): Promise<void>  {
          await new Promise((resolve) => setTimeout(resolve, WEBSOCKET_RECONNECT_INTERVAL_MS))
        }
      }))

      return acc
    }, {} as Record<Environment, GraphQLWsLink>)
)

export const ApolloClientStore = Object
  .values(Environment)
  .reduce<Record<Environment, ApolloClient>>((acc, env: Environment)=> {
    const httpOptions = {
      uri: getApiUrlByEnv(env),
      credentials: 'include' as RequestCredentials,
    }

    acc[env] = new ApolloClient({
      link: ApolloLink.split(
        splitTestFunc,
        ApolloLink.split(
          splitTestFunc,
          WSLinkStore[env],
          ApolloLink.from([errorLink, new HttpLink(httpOptions)])
        ),
        ApolloLink.from([errorLink, new UploadHttpLink(httpOptions)])
      ),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'network-only',
        },
      },
    })

    return acc
  }, {} as Record<Environment, ApolloClient>)

let defaultApolloClient: ApolloClient = ApolloClientStore[getDefaultClientEnv()]

export function getDefaultApolloClient(): ApolloClient {
  return defaultApolloClient
}

export function setDefaultApolloClient(env: Environment) {
  defaultApolloClient = ApolloClientStore[env]
  provideApolloClient(defaultApolloClient)
}
